import { MarketProgram } from '@/features/bidding/constants'
import type {
  AggregateBidOffer,
  AggregateBidOfferRequest,
  OfferedPtuChunk,
  PricedOfferPtu,
} from '@/features/bidding/types/aggregateBidOffer'
import type { BidLevelInput } from '@/features/bidding/types/bidLadderInput'
import type { BidLevelInputSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import { resolveVolumeCapacityForHour } from '@/features/bidding/utils/cascading/bidLadder/resolveVolumeCapacity'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

const PORTFOLIO_CODE = 'FI-A'
const RESOURCE_CATEGORY = 'BESS'
const AGGREGATION_LEVEL = 'PRICE_AREA' as const
const AGGREGATE_KEY = 'FI'
const MW_TO_WATTS = 1_000_000

function buildPtusForDirection(
  levels: BidLevelInput[],
  direction: 'up' | 'down',
  deliveryDay: MarketDate,
  suggestedMap: Map<number, number>,
  availableMap: Map<number, number>,
  granularityMW: number,
): PricedOfferPtu[] {
  const ptus: PricedOfferPtu[] = []
  const startOfDay = deliveryDay.getStartOfDay()

  for (let hour = 0; hour < 24; hour++) {
    const ptuStart = startOfDay.plus({ hours: hour })
    const ptuEnd = ptuStart.plus({ hours: 1 })

    const chunks: OfferedPtuChunk[] = []

    for (const level of levels) {
      const price = direction === 'up' ? level.upPrice : level.downPrice
      if (price == null) continue

      const volumeConfig = direction === 'up' ? level.upVolumeCapacity : level.downVolumeCapacity
      const volumeMW = resolveVolumeCapacityForHour(volumeConfig, hour, suggestedMap, availableMap, granularityMW)
      const volumeWatts = Math.round(volumeMW * MW_TO_WATTS)

      if (volumeWatts === 0) continue

      chunks.push({
        offeredVolume: { quantity: volumeWatts, unit: 'WATTS' },
        offeredPrice: price,
      })
    }

    if (chunks.length > 0) {
      ptus.push({
        ptu: {
          start: ptuStart.toISO()!,
          end: ptuEnd.toISO()!,
        },
        ptuChunks: chunks,
      })
    }
  }

  return ptus
}

export function buildAsymmetricalBidOfferRequest(
  levels: BidLevelInput[],
  deliveryDay: MarketDate,
  suggestedUpMap: Map<number, number>,
  suggestedDownMap: Map<number, number>,
  availableUpMap: Map<number, number>,
  availableDownMap: Map<number, number>,
  granularityMW: number,
): AggregateBidOfferRequest {
  const bids: AggregateBidOffer[] = []

  const upPtus = buildPtusForDirection(levels, 'up', deliveryDay, suggestedUpMap, availableUpMap, granularityMW)
  if (upPtus.length > 0) {
    bids.push({
      aggregateKey: AGGREGATE_KEY,
      marketProgram: MarketProgram.NORDICS_FCRD_UP_DYNAMIC,
      ptus: upPtus,
    })
  }

  const downPtus = buildPtusForDirection(levels, 'down', deliveryDay, suggestedDownMap, availableDownMap, granularityMW)
  if (downPtus.length > 0) {
    bids.push({
      aggregateKey: AGGREGATE_KEY,
      marketProgram: MarketProgram.NORDICS_FCRD_DOWN_DYNAMIC,
      ptus: downPtus,
    })
  }

  return {
    portfolioCode: PORTFOLIO_CODE,
    deliveryDay: deliveryDay.toISODate(),
    market: 'FCRD',
    resourceCategory: RESOURCE_CATEGORY,
    aggregationLevel: AGGREGATION_LEVEL,
    bids,
  }
}

export function buildSymmetricalBidOfferRequest(
  levels: BidLevelInputSymmetrical[],
  deliveryDay: MarketDate,
  suggestedMap: Map<number, number>,
  availableMap: Map<number, number>,
  granularityMW: number,
): AggregateBidOfferRequest {
  const bids: AggregateBidOffer[] = []
  const startOfDay = deliveryDay.getStartOfDay()

  const ptus: PricedOfferPtu[] = []

  for (let hour = 0; hour < 24; hour++) {
    const ptuStart = startOfDay.plus({ hours: hour })
    const ptuEnd = ptuStart.plus({ hours: 1 })

    const chunks: OfferedPtuChunk[] = []

    for (const level of levels) {
      if (level.price == null) continue

      const volumeMW = resolveVolumeCapacityForHour(
        level.volumeCapacity,
        hour,
        suggestedMap,
        availableMap,
        granularityMW,
      )
      const volumeWatts = Math.round(volumeMW * MW_TO_WATTS)

      if (volumeWatts === 0) continue

      chunks.push({
        offeredVolume: { quantity: volumeWatts, unit: 'WATTS' },
        offeredPrice: level.price,
      })
    }

    if (chunks.length > 0) {
      ptus.push({
        ptu: {
          start: ptuStart.toISO()!,
          end: ptuEnd.toISO()!,
        },
        ptuChunks: chunks,
      })
    }
  }

  if (ptus.length > 0) {
    bids.push({
      aggregateKey: AGGREGATE_KEY,
      marketProgram: MarketProgram.NORDICS_FCRN,
      ptus,
    })
  }

  return {
    portfolioCode: PORTFOLIO_CODE,
    deliveryDay: deliveryDay.toISODate(),
    market: 'FCRN',
    resourceCategory: RESOURCE_CATEGORY,
    aggregationLevel: AGGREGATION_LEVEL,
    bids,
  }
}
